import { useEffect, useState } from 'react'
import { app } from '@/lib/api/client'
import { useUserStore } from '@/stores/userStore'

// Who may use the city map editor (decision 2026-11-01): only the god account and shardmasters. GMs run campaigns on
// our maps; they do not edit them, and neither do players.
//
// The roles are the server's, from GET /api/me (getActorRoles in world-bff): the god account is the site admin
// ('admin', from SITE_ADMIN_EMAILS / SITE_ADMIN_SUBS / user_roles) and a shardmaster holds a `shardmaster:<shard id>`
// grant. 'gm' (SITE_GM_EMAILS, also granted alongside admin) does not open the editor on its own.
//
// This gate is about who sees and loads the tool. Writing to a living city is the Worker's to allow: its /v1/editor
// routes check the Atlas token's rank for each city (god, or a shardmaster of the city's shard) on every call, and
// list only the cities the caller may edit. Blank maps stay browser drafts and exported files.
export function canEditCityMaps(roles: readonly string[] | null | undefined): boolean {
  return !!roles?.some((r) => r === 'admin' || r.startsWith('shardmaster:'))
}

const cache = new Map<string, Promise<boolean>>()
const ask = (id: string) => {
  let p = cache.get(id)
  if (!p) {
    p = app.get<{ roles?: string[] }>('/me').then((me) => canEditCityMaps(me?.roles), () => { cache.delete(id); return false })
    cache.set(id, p)
  }
  return p
}

/** True when the signed-in account may use the map editor; null while the server is being asked. Signed out: false. */
export function useCanEditCityMaps(): boolean | null {
  const status = useUserStore((s) => s.status)
  const userId = useUserStore((s) => (s.status === 'authenticated' ? s.user?.id ?? null : null))
  const [allowed, setAllowed] = useState<{ id: string; ok: boolean } | null>(null)
  useEffect(() => {
    if (!userId) return
    let live = true
    void ask(userId).then((ok) => { if (live) setAllowed({ id: userId, ok }) })
    return () => { live = false }
  }, [userId])
  if (status === 'loading') return null
  if (!userId) return false
  return allowed?.id === userId ? allowed.ok : null
}
